import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Home as HomeIcon, FileText, Briefcase, Users, ArrowRight, Check, Star, ChevronLeft, ChevronRight, Award, Heart, Shield, Clock, MapPin, Building, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicLayout } from '@/components/PublicLayout';

const Home = () => {
  const { t, language } = useLanguage();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const services = [
    {
      icon: HomeIcon,
      title: t.services.housing.title,
      description: t.services.housing.description,
    },
    {
      icon: FileText,
      title: t.services.admin.title,
      description: t.services.admin.description,
    },
    {
      icon: Briefcase,
      title: t.services.job.title,
      description: t.services.job.description,
    },
    {
      icon: Users,
      title: t.services.integration.title,
      description: t.services.integration.description,
    },
  ];

  const howItWorksSteps = [
    {
      number: '1',
      title: language === 'fr' ? 'Inscription' : 'Registration',
      description: language === 'fr' 
        ? 'Devenez membre AILE en 5 minutes. Frais unique de 20€.' 
        : 'Become an AILE member in 5 minutes. One-time fee of €20.',
      icon: FileText
    },
    {
      number: '2',
      title: language === 'fr' ? 'Consultation' : 'Consultation',
      description: language === 'fr'
        ? 'Rencontrez notre équipe pour définir vos besoins et priorités.'
        : 'Meet our team to define your needs and priorities.',
      icon: Users
    },
    {
      number: '3',
      title: language === 'fr' ? 'Accompagnement' : 'Support',
      description: language === 'fr'
        ? 'Bénéficiez d\'un suivi personnalisé pour toutes vos démarches.'
        : 'Benefit from personalized support for all your procedures.',
      icon: Heart
    },
    {
      number: '4',
      title: language === 'fr' ? 'Installation' : 'Settlement',
      description: language === 'fr'
        ? 'Installez-vous sereinement dans votre nouveau logement à Lyon !'
        : 'Settle peacefully in your new home in Lyon!',
      icon: HomeIcon
    }
  ];

  const testimonials = [
    {
      name: 'Moussa Sidibe',
      country: language === 'fr' ? 'Mali' : 'Mali',
      photo: '/images/testimonial-1.jpg',
      text: language === 'fr'
        ? 'AILE m\'a sauvé la vie ! En 2 semaines, j\'avais mon logement et toutes mes démarches CAF étaient faites. L\'équipe est incroyable.'
        : 'AILE saved my life! In 2 weeks, I had my housing and all my CAF procedures were done. The team is amazing.',
      rating: 5
    },
    {
      name: 'Ahmed Benali',
      country: language === 'fr' ? 'Maroc' : 'Morocco',
      photo: '/images/testimonial-2.jpg',
      text: language === 'fr'
        ? 'Grâce à AILE, j\'ai trouvé un job étudiant en 1 mois et je me suis fait plein d\'amis internationaux. Je recommande à 200% !'
        : 'Thanks to AILE, I found a student job in 1 month and made lots of international friends. I recommend 200%!',
      rating: 5
    },
    {
      name: 'Julie AKA',
      country: language === 'fr' ? 'Côte d\'Ivoire' : 'Ivory Cost',
      photo: '/images/testimonial-3.jpg',
      text: language === 'fr'
        ? 'L\'accompagnement administratif est exceptionnel. Ils m\'ont aidé avec la préfecture et la sécurité sociale. Merci infiniment !'
        : 'The administrative support is exceptional. They helped me with the prefecture and social security. Thank you so much!',
      rating: 5
    }
  ];

  const whyAILE = [
    {
      icon: Award,
      title: language === 'fr' ? 'Expertise reconnue' : 'Recognized expertise',
      description: language === 'fr' ? 'Plus de 5 ans d\'expérience dans l\'accompagnement des étudiants internationaux' : 'Over 5 years of experience supporting international students'
    },
    {
      icon: Heart,
      title: language === 'fr' ? 'Accompagnement humain' : 'Human support',
      description: language === 'fr' ? 'Une équipe dédiée et bienveillante à votre écoute 7j/7' : 'A dedicated and caring team available 7 days a week'
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'Logements vérifiés' : 'Verified housing',
      description: language === 'fr' ? 'Tous nos logements sont contrôlés et conformes aux normes' : 'All our housing is inspected and meets standards'
    },
    {
      icon: Clock,
      title: language === 'fr' ? 'Gain de temps' : 'Time saving',
      description: language === 'fr' ? 'Trouvez votre logement 3x plus rapidement qu\'en cherchant seul' : 'Find your housing 3x faster than searching alone'
    }
  ];

  const stats = [
    {
      number: '500+',
      label: language === 'fr' ? 'Étudiants accompagnés' : 'Students helped'
    },
    {
      number: '95%',
      label: language === 'fr' ? 'Taux de satisfaction' : 'Satisfaction rate'
    },
    {
      number: '2 sem',
      label: language === 'fr' ? 'Délai moyen logement' : 'Average housing delay'
    },
    {
      number: '40+',
      label: language === 'fr' ? 'Pays représentés' : 'Countries represented'
    }
  ];

  const faqs = [
    {
      question: language === 'fr' ? 'Combien coûte l\'adhésion AILE ?' : 'How much does AILE membership cost?',
      answer: language === 'fr' 
        ? 'L\'adhésion est de 20€ (paiement unique) et vous donne accès à tous nos services : recherche de logement, accompagnement administratif, aide à l\'emploi et intégration.' 
        : 'Membership is €20 (one-time payment) and gives you access to all our services: housing search, administrative support, employment assistance and integration.'
    },
    {
      question: language === 'fr' ? 'Quels types de logements proposez-vous ?' : 'What types of housing do you offer?',
      answer: language === 'fr'
        ? 'Nous proposons des studios, T1, T2, T3 et colocations dans tous les quartiers de Lyon et Villeurbanne. Tous les logements sont vérifiés et adaptés aux budgets étudiants.'
        : 'We offer studios, 1-bedroom, 2-bedroom, 3-bedroom apartments and shared housing in all neighborhoods of Lyon and Villeurbanne. All housing is verified and suitable for student budgets.'
    },
    {
      question: language === 'fr' ? 'Aidez-vous pour les démarches CAF ?' : 'Do you help with CAF procedures?',
      answer: language === 'fr'
        ? 'Oui ! Nous vous accompagnons dans toutes vos démarches CAF : constitution du dossier, suivi de la demande et résolution des problèmes. Nous organisons même des sessions d\'information dédiées.'
        : 'Yes! We support you with all CAF procedures: file preparation, application follow-up and problem resolution. We even organize dedicated information sessions.'
    },
    {
      question: language === 'fr' ? 'Combien de temps faut-il pour trouver un logement ?' : 'How long does it take to find housing?',
      answer: language === 'fr'
        ? 'En moyenne, nos membres trouvent un logement en 2 semaines. Cela dépend de vos critères (budget, localisation, type) et de la période de l\'année.'
        : 'On average, our members find housing in 2 weeks. This depends on your criteria (budget, location, type) and the time of year.'
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-white via-primary-50/30 to-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="hero-content">
              <div className="inline-block mb-4 px-4 py-2 bg-primary-100 text-[#127abb] rounded-full text-sm font-semibold">
                ✨ {language === 'fr' ? 'Association à but non lucratif' : 'Non-profit organization'}
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6 leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services">
                  <Button
                    data-testid="cta-services-btn"
                    className="bg-[#127abb] hover:bg-[#0e6292] text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-8 py-6 text-base"
                  >
                    {t.hero.cta}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="cta-contact-btn"
                    variant="outline"
                    className="border-2 border-[#127abb]/20 hover:border-[#127abb] hover:bg-primary-50 text-[#127abb] rounded-full px-8 py-6 text-base"
                  >
                    {t.hero.ctaSecondary}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image Collage */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="/images/students-group.jpg"
                    alt="Students"
                    className="rounded-2xl shadow-xl w-full h-48 object-cover"
                  />
                  <img
                    src="/images/apartment-interior.jpg"
                    alt="Apartment"
                    className="rounded-2xl shadow-xl w-full h-64 object-cover"
                  />
                </div>
                <div className="mt-8">
                  <img
                    src="/images/lyon-city.jpg"
                    alt="Lyon"
                    className="rounded-2xl shadow-xl w-full h-80 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-[#127abb] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#127abb] via-[#0e6292] to-[#127abb] opacity-50"></div>
        <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {stats.map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`} className="transform hover:scale-105 transition-transform">
                <div className="text-5xl font-bold mb-2">{stat.number}</div>
                <p className="text-primary-100 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Comment ça marche ?' : 'How it works?'}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {language === 'fr' 
                ? 'Un processus simple et rapide pour vous installer à Lyon en toute sérénité' 
                : 'A simple and fast process to settle in Lyon with peace of mind'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="relative bg-white border-2 border-stone-100 shadow-lg hover:shadow-2xl transition-all duration-300 p-8 rounded-2xl hover:-translate-y-2"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#127abb] text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    {step.number}
                  </div>
                  <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center mb-6 mx-auto">
                    <Icon className="text-[#127abb]" size={32} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 text-center">{step.title}</h3>
                  <p className="text-slate-600 text-center leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              {t.services.title}
            </h2>
            <p className="text-xl text-slate-600">{t.services.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  data-testid={`service-card-${index}`}
                  className="bg-white border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-300 p-8 rounded-2xl hover:-translate-y-2 group"
                >
                  <div className="w-14 h-14 bg-primary-50 group-hover:bg-[#127abb] rounded-full flex items-center justify-center mb-6 transition-colors">
                    <Icon className="text-[#127abb] group-hover:text-white transition-colors" size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                className="bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full px-8 py-3 shadow-lg"
              >
                {language === 'fr' ? 'Voir tous les services' : 'View all services'}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why AILE */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Pourquoi choisir AILE ?' : 'Why choose AILE?'}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Les avantages qui font la différence pour votre installation à Lyon'
                : 'The advantages that make the difference for your settlement in Lyon'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyAILE.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-xl transition-all duration-300 border border-transparent hover:border-primary-100"
                >
                  <div className="w-16 h-16 bg-[#127abb] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={32} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Ils nous font confiance' : 'They trust us'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' 
                ? 'Découvrez les témoignages de nos étudiants' 
                : 'Discover testimonials from our students'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto relative">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="flex items-center gap-6 mb-6">
                <img
                  src={testimonials[currentTestimonial].photo}
                  alt={testimonials[currentTestimonial].name}
                  className="w-20 h-20 rounded-full object-cover border-4 border-primary-100"
                />
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{testimonials[currentTestimonial].name}</h3>
                  <p className="text-slate-600">{testimonials[currentTestimonial].country}</p>
                  <div className="flex gap-1 mt-2">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-lg text-slate-700 leading-relaxed italic">
                "{testimonials[currentTestimonial].text}"
              </p>
            </div>

            {/* Navigation */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#127abb] hover:text-white transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#127abb] hover:text-white transition-colors"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial 
                      ? 'bg-[#127abb] w-8' 
                      : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' 
                ? 'Tout ce que vous devez savoir sur AILE' 
                : 'Everything you need to know about AILE'}
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-slate-50 hover:bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</h3>
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-50 group-open:bg-[#127abb] rounded-full flex items-center justify-center transition-colors">
                    <ArrowRight className="text-[#127abb] group-open:text-white group-open:rotate-90 transition-transform" size={20} />
                  </div>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-4">
              {language === 'fr' ? 'Vous avez d\'autres questions ?' : 'Do you have other questions?'}
            </p>
            <Link to="/contact">
              <Button className="bg-[#127abb] hover:bg-[#0e6292] rounded-full px-8 py-3">
                {language === 'fr' ? 'Contactez-nous' : 'Contact us'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#127abb]/20 to-[#8c1616]/10"></div>
        <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'fr' ? 'Prêt à commencer votre aventure à Lyon ?' : 'Ready to start your adventure in Lyon?'}
            </h2>
            <p className="text-xl text-slate-300 mb-10">
              {language === 'fr'
                ? 'Rejoignez les centaines d\'étudiants qui ont fait confiance à AILE pour leur installation en France'
                : 'Join the hundreds of students who trusted AILE for their settlement in France'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="cta-bottom-btn"
                  size="lg"
                  className="bg-white text-[#127abb] hover:bg-slate-100 rounded-full px-10 py-7 text-lg shadow-2xl"
                >
                  {language === 'fr' ? 'Devenir membre' : 'Become a member'}
                </Button>
              </Link>
              <Link to="/listings">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-7 text-lg"
                >
                  {language === 'fr' ? 'Voir les logements' : 'View housing'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Home;